'use client';

import { useEffect, useState } from 'react';

import styles from './LandingEarthPeek.module.scss';

const TEST_ID_PREFIX = 'components-landing-earth-peek';
const POSTER_SRC = '/landing/earth-peek-poster.webp';
const WEBM_SRC = '/landing/earth-peek.webm';
const MP4_SRC = '/landing/earth-peek.mp4';
const LOW_BANDWIDTH_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

type EarthMediaState = 'loading' | 'poster' | 'ready';

interface LandingEarthMotionContext {
    effectiveType: string | null;
    prefersReducedMotion: boolean;
    saveData: boolean;
}

interface NavigatorConnection {
    effectiveType?: string;
    saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
    connection?: NavigatorConnection;
}

export const shouldEnableLandingEarthMotion = ({ effectiveType, prefersReducedMotion, saveData }: LandingEarthMotionContext): boolean => {
    if (prefersReducedMotion || saveData) {
        return false;
    }

    return !LOW_BANDWIDTH_EFFECTIVE_TYPES.has(effectiveType ?? '');
};

const getLandingEarthMotionContext = (): LandingEarthMotionContext => {
    const connection = (navigator as NavigatorWithConnection).connection;
    const prefersReducedMotion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

    return {
        effectiveType: connection?.effectiveType ?? null,
        prefersReducedMotion,
        saveData: connection?.saveData === true,
    };
};

export const LandingEarthPeek = () => {
    const [isVideoEnabled, setIsVideoEnabled] = useState(false);
    const [mediaState, setMediaState] = useState<EarthMediaState>('loading');

    useEffect(() => {
        if (!shouldEnableLandingEarthMotion(getLandingEarthMotionContext())) {
            const posterHandle = window.setTimeout(() => setMediaState('poster'), 0);

            return () => window.clearTimeout(posterHandle);
        }

        let didCancel = false;

        const enableVideo = () => {
            if (!didCancel) {
                setMediaState('loading');
                setIsVideoEnabled(true);
            }
        };

        const videoHandle = window.setTimeout(enableVideo, 0);

        return () => {
            didCancel = true;
            window.clearTimeout(videoHandle);
        };
    }, []);

    return (
        <div aria-hidden="true" className={styles.root} data-testid={`${TEST_ID_PREFIX}-root`}>
            <div className={styles.mediaFrame} data-earth-state={mediaState} data-testid={`${TEST_ID_PREFIX}-media-frame`}>
                <img alt="" className={styles.poster} data-testid={`${TEST_ID_PREFIX}-poster`} decoding="async" draggable={false} src={POSTER_SRC} />
                {isVideoEnabled && (
                    <video
                        aria-hidden="true"
                        autoPlay
                        className={styles.video}
                        data-testid={`${TEST_ID_PREFIX}-video`}
                        loop
                        muted
                        onCanPlay={() => setMediaState('ready')}
                        onError={() => setMediaState('poster')}
                        onLoadedData={() => setMediaState('ready')}
                        playsInline
                        poster={POSTER_SRC}
                        preload="auto"
                    >
                        <source src={WEBM_SRC} type="video/webm" />
                        <source src={MP4_SRC} type="video/mp4" />
                    </video>
                )}
            </div>
        </div>
    );
};

LandingEarthPeek.displayName = 'LandingEarthPeek';
